import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { KeyRound, CheckCircle2, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/verify-reset-token/${token}`);
      if (res.data.valid) {
        setTokenValid(true);
        setUserEmail(res.data.email || '');
        setUserName(res.data.name || '');
      }
    } catch (err) {
      console.warn('Reset token verification failed', err);
    } finally {
      setVerifying(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { token, password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-orange-50" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <img src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png" alt="A&W" className="w-12 h-12" />
          <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Elmwood</span>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
          {verifying ? (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-orange-500 mb-4" />
              <p className="text-slate-500">Verifying reset link...</p>
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Password Reset!</h1>
              <p className="text-slate-500 mb-6">Your password has been updated successfully. You can now sign in with your new password.</p>
              <Button onClick={() => navigate('/login')} className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-6 rounded-xl shadow-lg shadow-orange-200" data-testid="go-to-login-btn">
                Go to Login <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          ) : !token || !tokenValid ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Invalid or Expired Link</h1>
              <p className="text-slate-500 mb-6">This password reset link is invalid or has expired. Please request a new one.</p>
              <Button onClick={() => navigate('/login')} variant="outline" className="w-full py-6 rounded-xl" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-8 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <KeyRound className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900">Reset Your Password</h1>
                <p className="mt-2 text-slate-500">
                  {userName ? `Hi ${userName}, enter` : 'Enter'} your new password below
                </p>
                {userEmail && (
                  <p className="mt-1 text-sm text-slate-400">{userEmail}</p>
                )}
              </div>
              <form onSubmit={handleReset} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-slate-700">New Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Type it again"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-6 rounded-xl shadow-lg shadow-orange-200"
                  disabled={loading}
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
                  ) : (
                    <>Reset Password <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-slate-400">Need help? Contact support@elmwoodgroups.com</p>
        </div>
      </div>
    </div>
  );
}
